export interface RuntimeSprite {
    canvas: HTMLCanvasElement;
    logicalWidth: number;
    logicalHeight: number;
}

const queueMarkerCache = new Map<string, RuntimeSprite>();
const chestMarkerCache = new Map<string, RuntimeSprite>();
const npcMarkerCache = new Map<string, RuntimeSprite>();

function getDevicePixelRatio(): number {
    if (typeof window === 'undefined' || !Number.isFinite(window.devicePixelRatio)) {
        return 1;
    }
    return Math.max(1, window.devicePixelRatio);
}

function createCanvas(width: number, height: number): RuntimeSprite {
    const scale = getDevicePixelRatio();
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(width * scale));
    canvas.height = Math.max(1, Math.ceil(height * scale));
    return {
        canvas,
        logicalWidth: width,
        logicalHeight: height,
    };
}

export function getQueueMarkerSprite(label: string): RuntimeSprite {
    const scale = getDevicePixelRatio();
    const key = `${label}:${scale}`;
    const existing = queueMarkerCache.get(key);
    if (existing) {
        return existing;
    }

    const size = 32;
    const sprite = createCanvas(size, size);
    const ctx = sprite.canvas.getContext('2d');
    if (!ctx) {
        return sprite;
    }
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, 15, 0, Math.PI * 2);
    ctx.fillStyle = '#1e293b';
    ctx.fill();
    ctx.fillStyle = '#f8fafc';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, size / 2, size / 2);
    queueMarkerCache.set(key, sprite);
    return sprite;
}

export function getChestMarkerSprite(): RuntimeSprite {
    const scale = getDevicePixelRatio();
    const key = `default:${scale}`;
    const existing = chestMarkerCache.get(key);
    if (existing) {
        return existing;
    }

    const size = 20;
    const sprite = createCanvas(size, size);
    const ctx = sprite.canvas.getContext('2d');
    if (!ctx) {
        return sprite;
    }
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    ctx.fillStyle = 'rgba(255, 215, 0, 0.85)';
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = '#8B6914';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, size, size);
    ctx.fillStyle = '#3a1a00';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('C', size / 2, size / 2);
    chestMarkerCache.set(key, sprite);
    return sprite;
}

export function getNpcMarkerSprite(emoji: string, widthPx: number, heightPx: number): RuntimeSprite {
    const scale = getDevicePixelRatio();
    const key = `${emoji}:${Math.round(widthPx)}:${Math.round(heightPx)}:${scale}`;
    const existing = npcMarkerCache.get(key);
    if (existing) {
        return existing;
    }

    const sprite = createCanvas(widthPx, heightPx);
    const ctx = sprite.canvas.getContext('2d');
    if (!ctx) {
        return sprite;
    }
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    ctx.fillStyle = 'rgba(142, 68, 173, 0.3)';
    ctx.strokeStyle = 'rgba(44, 62, 80, 0.5)';
    ctx.lineWidth = 1;
    ctx.fillRect(0, 0, widthPx, heightPx);
    ctx.strokeRect(0, 0, widthPx, heightPx);
    const fontSize = Math.min(widthPx, heightPx) * 0.6;
    ctx.font = `${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'white';
    ctx.fillText(emoji, widthPx / 2, heightPx / 2);
    npcMarkerCache.set(key, sprite);
    return sprite;
}
